import { createContext, useContext, useEffect } from 'react';

/**
 * What a screen tells the assistant about itself.
 *
 * A screen PUBLISHES a snapshot — it is never scraped. That is the whole
 * design: the assistant can only see values a screen deliberately exposed, so
 * a customer's details cannot reach the model because someone passed a view
 * model around. The server enforces the same shape again (scalars only,
 * capped) in controller/helpCont.js.
 *
 * Split from the provider component so this file exports no components — the
 * same shape `screens/comms/context.ts` uses.
 */
export interface HelpPageSnapshot {
  /** What the screen calls itself — "Channel settings", "Template detail". */
  title?: string;
  /** The kind of thing on screen, if there is one: 'inbox', 'template'. */
  entity?: string;
  id?: string | number;
  /** Flat, scalar, debugging-relevant. No objects, no arrays, no PII. */
  state?: Record<string, string | number | boolean | null | undefined>;
}

export interface HelpPageValue {
  snapshot: HelpPageSnapshot | null;
  publish: (snapshot: HelpPageSnapshot | null) => void;
}

export const HelpPageContext = createContext<HelpPageValue>({
  snapshot: null,
  publish: () => {},
});

/** Read the current snapshot — the launcher sends it with each question. */
export const useHelpPage = (): HelpPageSnapshot | null => useContext(HelpPageContext).snapshot;

/**
 * Publish from a screen. Clears on unmount, so a screen the operator has left
 * can never describe itself to a question asked somewhere else.
 */
export function usePublishHelpPage(snapshot: HelpPageSnapshot | null): void {
  const { publish } = useContext(HelpPageContext);
  // Serialized rather than referentially compared: callers build the object
  // inline, and a fresh object every render would re-publish forever.
  const key = snapshot ? JSON.stringify(snapshot) : '';
  useEffect(() => {
    publish(key ? (JSON.parse(key) as HelpPageSnapshot) : null);
    return () => publish(null);
  }, [key, publish]);
}
